import { ReactNode } from 'react';
import { Button } from '@nf/ui';
import { Icon } from '@/components/icons/svg-icons';
import { ReactPortal } from '@/components/widgets/react-portal';
import clsx from 'clsx';
import { 
	modalCloseBtn, 
	modalContainer,
	ModalContainerVariantProps, 
	modalContentArea, 
	modalOverlay, 
	modalTitle, 
	scrollableModal 
} from 'styled-system/recipes';
import { AutoPopupKey, useRegisterAutoPopup } from '@nf/auto-popup-handler';

// 使用pandas recipes撰寫, 透過flex屬性擠壓元素使得modal可置中及產生scrollbar
export const ModalHeader = ({ 
	className,
	title, 
	isShowClose = true,
	closeModal 
}: {
	className?: string;
	title?: string;
	isShowClose?: boolean;
	closeModal?: () => void;
}) => {
	return (
		<div className={className}>
			{title && title.length > 0 && (
				<div className={modalTitle()}>
					{title}
				</div>
			)}
			{ isShowClose ? (
				<div className={modalCloseBtn()}>
					<Button variant="icon" onClick={closeModal}>
						<Icon name="close-line" />
					</Button>
				</div>
			) : null}
		</div>
	)
}

export const BasicModalHeader = ({ 
	className,
	title, 
	isShowClose = true,
	closeModal 
}: {
	className?: string;
	title?: string;
	isShowClose?: boolean;
	closeModal?: () => void;
}) => {
	return (
		<div className={clsx('modal__header', className)}>
			{title && title.length > 0 && (
				<div className="modal__title">
					{title}
				</div>
			)}
			{ isShowClose ? (
				<div className="modal__close-btn">
					<Button variant="icon" onClick={closeModal}>
						<Icon name="close-line" />
					</Button>
				</div>
			) : null}
		</div>
	)
}

export const ModalContent = ({
	children
}: {
	children: ReactNode;
}) => {
	return (
		<div className={modalContentArea()}>
			{children}
		</div>
	)
}

export const ModalFooter = ({
	children
}: {
	children: ReactNode;
}) => {
	return (
		<div>
			{children}
		</div>
	)
}

const ModalContainer = ({
	isOpen,
	closeModal,
	children,
	className,
	modalContainerVariantProps,
	sectionName,
	isBackdropClose = true,
	
}: {
	isOpen :boolean;
	closeModal: () => void;
	children: ReactNode;
	className?: string;
	modalContainerVariantProps?: ModalContainerVariantProps;
	sectionName?: string,
	isBackdropClose?: boolean;
}) => {
	const handleBackgropClose = () => {
		isBackdropClose ? closeModal() : null
	}
	return isOpen ? (
		<ReactPortal wrapperId="modal-portal">
			<section className={clsx(scrollableModal(), sectionName)}>
				<div className={modalOverlay()} onClick={handleBackgropClose}></div>
				<div className={clsx(modalContainer(modalContainerVariantProps), className)}>
					{children}
				</div>
			</section>  
		</ReactPortal>
	): null;
};

interface ScrollableModalProps {
	isOpen: boolean;
	closeModal: () => void;
	className?: string;
	children: ReactNode;
	header?: ReactNode;
	footer?: ReactNode;
	sectionName?: string;
}

interface AutoPopupScrollableModalProps extends ScrollableModalProps {
	popupKey: AutoPopupKey;
}

export const ScrollableModal = ({
	isOpen, 
	closeModal,
	className,
	children,
	header,
	footer,
	sectionName,
}: ScrollableModalProps) => {
	return (
		<ModalContainer
			sectionName={sectionName}
			className={className}
			isOpen={isOpen}
			closeModal={closeModal}
			modalContainerVariantProps={{ flexOrientation: 'column' }}
		>
			{header}
			<ModalContent>
				{children}
			</ModalContent>
			{footer}
		</ModalContainer>
	)
}

export const AutoPopupScrollableModal = ({
	popupKey,
	isOpen,
	...props
}: AutoPopupScrollableModalProps) => {
	const canShow = useRegisterAutoPopup({
		key: popupKey,
		isEnabled: isOpen,
	});

	return (
		<ScrollableModal {...props} isOpen={canShow} />
	);
};
